import { ThunderboltOutlined } from '@ant-design/icons'
import { NetworkStatus } from '@apollo/client'
import { Button, Spin, Timeline } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'

import { PostType, useGetUserTimelineQuery } from '@/generated/graphql'

import { UserTimelineItem } from './user-timeline-item.component'
import s from './user-timeline.module.less'

interface UserTimelineProps {
  userId: number
}

export const UserTimeline: React.FC<UserTimelineProps> = ({ userId }) => {
  const [t] = useTranslation()

  const paginationCount = 20
  const [showSimplified, setSimplified] = React.useState(false)
  const [type, setType] = React.useState<PostType | 'AnniversaryCard' | 'All'>('All')
  const { data, error, fetchMore, networkStatus } = useGetUserTimelineQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      type,
      userId,
      pagination: {
        last: paginationCount,
      },
    },
  })

  if (error) {
    return null
  }

  const loading = networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables
  const loadingMore = networkStatus === NetworkStatus.fetchMore
  const pageInfo = data?.user.timelineEvents.pageInfo || {
    startCursor: null,
    endCursor: null,
    hasPreviousPage: false,
    hasNextPage: false,
  }

  const edges = (data?.user.timelineEvents.edges || [])
    .map(({ node }) => node)
    .slice()
    .reverse()

  const timeline = (
    <Timeline>
      {edges
        .filter((e) => e.entity)
        .map((e) => (
          <UserTimelineItem
            userId={userId}
            showSimplified={showSimplified}
            key={e.userTimelineEventId}
            event={e}
            setTimelineItemType={setType}
          />
        ))}
    </Timeline>
  )
  const loadingComponent = (loading || loadingMore) && (
    <div className={s.loading}>
      <Spin size='large' />
    </div>
  )
  const simplifiedButton = (
    <Button type={'primary'} onClick={() => setSimplified(!showSimplified)}>
      {showSimplified ? t('timeline.simplified') : t('timeline.fullExpand')}
      <ThunderboltOutlined />
    </Button>
  )

  const infiniteScroll = !loading && (
    <InfiniteScroll
      dataLength={edges.filter((e) => e.entity).length}
      loader={loadingComponent}
      className={s.timeline}
      style={{ height: undefined, overflow: undefined }}
      hasMore={pageInfo.hasPreviousPage}
      next={() => {
        fetchMore({
          variables: {
            pagination: {
              last: paginationCount,
              before: pageInfo.startCursor,
            },
          },
        })
      }}
    >
      {timeline}
    </InfiniteScroll>
  )

  return (
    <div className={s.container}>
      <div className={s.action}>{simplifiedButton}</div>
      {infiniteScroll}
    </div>
  )
}
